import React, { useEffect, useRef, useState, useCallback } from "react";
import { Card, CardBody, Container, Row, Col, Button, Badge, Spinner, Input, Modal, ModalHeader, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import { GetData, PostData, DeleteData } from "../../services/api";

// Página: Conectar la cuenta de WhatsApp de la compañía vía Meta Embedded Signup + compartir números.
const WhatsAppProviders = () => {
  const [config, setConfig] = useState(null); // { appId, configId, graphVersion }
  const [status, setStatus] = useState(null); // { connected, alive, numbers: [] }
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [fbReady, setFbReady] = useState(false);
  const sessionRef = useRef({}); // { phone_number_id, waba_id } capturados del Embedded Signup

  // --- Compartir / importar ---
  const [grants, setGrants] = useState({ authorized: [], sharedWithMe: [] });
  const [importCode, setImportCode] = useState("");
  const [importing, setImporting] = useState(false);
  const [exportModal, setExportModal] = useState(null); // { code, expiresAt, number }

  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("authUser") || "{}");
      if (u.role) return u.role === "admin";
      const payload = JSON.parse(atob((u.token || "").split(".")[1] || ""));
      return payload.role === "admin";
    } catch (_) { return false; }
  })();

  const loadStatus = useCallback(async () => {
    try {
      const r = await GetData("/whatsapp/connect/status");
      if (r?.data) setStatus(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadGrants = useCallback(async () => {
    try {
      const r = await GetData("/whatsapp/share/grants");
      if (r?.data) setGrants({ authorized: r.data.authorized || [], sharedWithMe: r.data.sharedWithMe || [] });
    } catch (e) { /* ignore */ }
  }, []);

  // Carga config + estado, e inicializa el SDK de Facebook.
  useEffect(() => {
    (async () => {
      try {
        const cfg = await GetData("/whatsapp/connect/config");
        if (cfg?.data?.appId && cfg?.data?.configId) {
          setConfig(cfg.data);
          initFacebookSdk(cfg.data.appId, cfg.data.graphVersion || "v19.0");
        } else {
          setNotConfigured(true);
        }
      } catch (e) {
        setNotConfigured(true);
      }
      await loadStatus();
      await loadGrants();
      setLoading(false);
    })();
  }, [loadStatus, loadGrants]);

  // Escucha los mensajes de la sesión de Embedded Signup (trae phone_number_id + waba_id).
  useEffect(() => {
    const onMessage = (event) => {
      if (!String(event.origin).endsWith("facebook.com")) return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "WA_EMBEDDED_SIGNUP" && data?.data) {
          sessionRef.current = { phone_number_id: data.data.phone_number_id, waba_id: data.data.waba_id };
        }
      } catch (_) { /* no-op */ }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const initFacebookSdk = (appId, version) => {
    const sdkVersion = "v21.0";
    const doInit = () => {
      try {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: sdkVersion });
        setFbReady(true);
        console.log("[whatsapp] FB SDK inicializado", { appId, sdkVersion });
      } catch (e) {
        console.error("[whatsapp] FB.init falló", e);
      }
    };
    if (window.FB) { doInit(); return; }
    window.fbAsyncInit = doInit;
    if (!document.getElementById("facebook-jssdk")) {
      const js = document.createElement("script");
      js.id = "facebook-jssdk";
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      js.async = true;
      js.defer = true;
      js.crossOrigin = "anonymous";
      js.onerror = () => {
        console.error("[whatsapp] No se pudo cargar el SDK de Facebook (¿bloqueado por CSP/adblock?)");
        toast.error("No se pudo cargar el SDK de Facebook (revisa adblock/red).");
      };
      document.body.appendChild(js);
    }
  };

  const handleConnect = () => {
    if (!config?.configId) { toast.error("Falta la configuración de Embedded Signup (configId)."); return; }
    if (!window.FB || !fbReady) {
      toast.error("El SDK de Facebook aún no cargó. Espera unos segundos y reintenta.");
      console.warn("[whatsapp] FB no listo", { hasFB: !!window.FB, fbReady });
      return;
    }
    setConnecting(true);
    sessionRef.current = {};
    const safety = setTimeout(() => setConnecting(false), 120000);
    const finish = () => { clearTimeout(safety); setConnecting(false); };
    const onLogin = (response) => {
      console.log("[whatsapp] FB.login response", response, "session:", sessionRef.current);
      (async () => {
        try {
          const code = response?.authResponse?.code;
          if (!code) { toast.error("No se completó la autorización de WhatsApp (revisa consola)."); return; }
          const r = await PostData("/whatsapp/connect/exchange", {
            code, wabaId: sessionRef.current.waba_id, phoneNumberId: sessionRef.current.phone_number_id,
          });
          if (r?.data?.ok) {
            toast.success(`WhatsApp conectado (${r.data.number || r.data.phoneNumberId}). Plantillas: ${r.data.syncedTemplates ?? 0}.`);
            await loadStatus();
          } else { toast.error("No se pudo guardar la conexión."); }
        } catch (e) {
          toast.error(e?.response?.data?.error?.message || e?.message || "Error al conectar WhatsApp.");
        } finally { finish(); }
      })();
    };
    try {
      window.FB.login(onLogin, {
        config_id: config.configId, response_type: "code", override_default_response_type: true,
        extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
      });
    } catch (e) {
      console.error("[whatsapp] FB.login lanzó excepción", e);
      toast.error("No se pudo abrir el popup de conexión (revisa consola).");
      finish();
    }
  };

  const handleSync = async () => {
    try {
      const r = await PostData("/whatsapp/templates/sync", {});
      toast.success(`Plantillas sincronizadas: ${r?.data?.synced ?? 0}.`);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "No se pudieron sincronizar las plantillas.");
    }
  };

  const handleDisconnect = async (phoneNumberId) => {
    if (!window.confirm("¿Desconectar esta cuenta de WhatsApp?")) return;
    try {
      await DeleteData(`/whatsapp/connect/${encodeURIComponent(phoneNumberId)}`);
      toast.success("WhatsApp desconectado.");
      await loadStatus();
    } catch (e) { toast.error("No se pudo desconectar."); }
  };

  // Genera un código para autorizar el uso de una línea a otra compañía.
  const handleExport = async (n) => {
    try {
      const r = await PostData("/whatsapp/share/export", { phoneNumberId: n.phoneNumberId });
      if (r?.data?.code) setExportModal({ code: r.data.code, expiresAt: r.data.expiresAt, number: r.data.number || n.number });
      else toast.error("No se pudo generar el código.");
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "No se pudo generar el código.");
    }
  };

  // Importa un código recibido de otra compañía.
  const handleImport = async () => {
    const code = importCode.trim();
    if (!code) return;
    setImporting(true);
    try {
      const r = await PostData("/whatsapp/share/import", { code });
      if (r?.data?.ok) {
        toast.success(`Número importado (${r.data.number || r.data.phoneNumberId}). Ya puedes operarlo.`);
        setImportCode("");
        await loadStatus();
        await loadGrants();
      } else { toast.error("No se pudo importar."); }
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Código inválido o expirado.");
    } finally { setImporting(false); }
  };

  const handleRevoke = async (phoneNumberId, grantedCompanyId) => {
    if (!window.confirm("¿Revocar este acceso compartido?")) return;
    try {
      await DeleteData(`/whatsapp/share/${encodeURIComponent(phoneNumberId)}/${encodeURIComponent(grantedCompanyId)}`);
      toast.success("Acceso revocado.");
      await loadGrants();
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "No se pudo revocar.");
    }
  };

  const copy = (t) => { try { navigator.clipboard.writeText(t); toast.success("Copiado."); } catch (_) {} };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <h4 className="mb-1">WhatsApp</h4>
            <p className="text-muted mb-0">Conecta la cuenta de WhatsApp de tu empresa para enviar mensajes automatizados y plantillas desde tus flujos.</p>
          </Col>
        </Row>

        {loading ? (
          <Spinner />
        ) : notConfigured ? (
          <Card><CardBody>
            <h5>WhatsApp no está configurado</h5>
            <p className="text-muted mb-0">Falta la configuración de Meta (App ID / Embedded Signup) en el servidor. Contacta al administrador.</p>
          </CardBody></Card>
        ) : (
          <Row>
            <Col lg={7}>
              <Card><CardBody>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-1">Cuenta de WhatsApp</h5>
                    {status?.connected ? (
                      <Badge color={status.alive ? "success" : "warning"}>
                        {status.alive ? "Conectada" : "Conectada (verificar)"}
                      </Badge>
                    ) : (
                      <Badge color="secondary">No conectada</Badge>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    {status?.connected && (
                      <Button color="light" onClick={handleSync}>Sincronizar plantillas</Button>
                    )}
                    <Button color="success" onClick={handleConnect} disabled={connecting}>
                      {connecting ? "Conectando…" : status?.connected ? "Conectar otra" : "Conectar WhatsApp"}
                    </Button>
                  </div>
                </div>

                <hr />

                {status?.numbers?.length ? (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead>
                        <tr><th>Número</th><th>Phone Number ID</th><th>WABA</th><th></th></tr>
                      </thead>
                      <tbody>
                        {status.numbers.map((n) => (
                          <tr key={n.phoneNumberId}>
                            <td><b>{n.number}</b></td>
                            <td className="text-muted">{n.phoneNumberId}</td>
                            <td className="text-muted">{n.wabaId}</td>
                            <td className="text-end">
                              {isAdmin && (
                                <Button size="sm" color="outline-primary" className="me-2" onClick={() => handleExport(n)} title="Autorizar a otra compañía a usar este número">
                                  Exportar acceso
                                </Button>
                              )}
                              <Button size="sm" color="outline-danger" onClick={() => handleDisconnect(n.phoneNumberId)}>Desconectar</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted mb-0">Aún no has conectado una cuenta. Haz clic en «Conectar WhatsApp» y autoriza tu número en el popup de Meta.</p>
                )}
              </CardBody></Card>

              {isAdmin && (
                <Card><CardBody>
                  <h5 className="mb-1">Importar número compartido</h5>
                  <p className="text-muted">Pega el código que te compartió el propietario de un número para poder operarlo desde tu cuenta.</p>
                  <div className="d-flex gap-2">
                    <Input type="textarea" rows={2} placeholder="Pega aquí el código de exportación…" value={importCode} onChange={(e) => setImportCode(e.target.value)} />
                    <Button color="primary" onClick={handleImport} disabled={importing || !importCode.trim()} style={{ whiteSpace: "nowrap" }}>
                      {importing ? "Importando…" : "Importar"}
                    </Button>
                  </div>
                </CardBody></Card>
              )}

              {isAdmin && (grants.authorized.length > 0 || grants.sharedWithMe.length > 0) && (
                <Card><CardBody>
                  {grants.authorized.length > 0 && (
                    <>
                      <h6 className="mb-2">Accesos que autoricé</h6>
                      <div className="table-responsive mb-3">
                        <table className="table table-sm align-middle mb-0">
                          <thead><tr><th>Número</th><th>Compañía autorizada</th><th></th></tr></thead>
                          <tbody>
                            {grants.authorized.map((g) => (
                              <tr key={g.id}>
                                <td className="text-muted">{g.phoneNumberId}</td>
                                <td><b>{g.companyName}</b></td>
                                <td className="text-end">
                                  <Button size="sm" color="outline-danger" onClick={() => handleRevoke(g.phoneNumberId, g.grantedCompanyId)}>Revocar</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  {grants.sharedWithMe.length > 0 && (
                    <>
                      <h6 className="mb-2">Números que me compartieron</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead><tr><th>Número</th><th>Propietario</th><th></th></tr></thead>
                          <tbody>
                            {grants.sharedWithMe.map((g) => (
                              <tr key={g.id}>
                                <td className="text-muted">{g.phoneNumberId}</td>
                                <td><b>{g.companyName}</b></td>
                                <td className="text-end">
                                  <Button size="sm" color="outline-secondary" onClick={() => handleRevoke(g.phoneNumberId, g.grantedCompanyId)}>Quitar</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardBody></Card>
              )}
            </Col>

            <Col lg={5}>
              <Card><CardBody>
                <h6>¿Qué se conecta?</h6>
                <ul className="text-muted mb-0" style={{ paddingLeft: 18 }}>
                  <li>Tu número de WhatsApp Business (Cloud API).</li>
                  <li>Se sincronizan tus plantillas aprobadas para usarlas en flujos.</li>
                  <li>Los mensajes se envían con tu número desde las automatizaciones.</li>
                </ul>
              </CardBody></Card>

              {isAdmin && (
                <Card><CardBody>
                  <h6>Compartir un número</h6>
                  <ul className="text-muted mb-0" style={{ paddingLeft: 18 }}>
                    <li>«Exportar acceso» genera un <b>código temporal</b> (30 min) para otra compañía.</li>
                    <li>Ambas compañías pueden <b>enviar</b> por el mismo número.</li>
                    <li>Cada quien ve solo las conversaciones de <b>sus leads registrados</b>.</li>
                    <li>Puedes <b>revocar</b> el acceso cuando quieras.</li>
                  </ul>
                </CardBody></Card>
              )}
            </Col>
          </Row>
        )}
      </Container>

      <Modal isOpen={!!exportModal} toggle={() => setExportModal(null)}>
        <ModalHeader toggle={() => setExportModal(null)}>Código de acceso · {exportModal?.number}</ModalHeader>
        <ModalBody>
          <p className="text-muted">Comparte este código con la otra compañía. Es válido por 30 minutos. Al importarlo, esa compañía podrá operar este número (tú puedes revocarlo cuando quieras).</p>
          <Input type="textarea" rows={4} readOnly value={exportModal?.code || ""} onFocus={(e) => e.target.select()} />
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">Expira: {exportModal?.expiresAt ? new Date(exportModal.expiresAt).toLocaleString() : ""}</small>
            <Button color="primary" onClick={() => copy(exportModal?.code)}>Copiar código</Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default WhatsAppProviders;
